import React from 'react';
import Navbar from '../layout/Navbar';
import Footer from '../layout/Footer';
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
    const sections = [
        {
            title: "1. Introduction",
            content: "Welcome to Trauma Centre BHU. We are committed to protecting your privacy and ensuring you have a positive experience on our website and when using our services. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website."
        },
        {
            title: "2. Information We Collect",
            subsections: [
                {
                    subtitle: "Personal Information You Provide:",
                    items: [
                        "Name and contact information (email, phone number, address)",
                        "Medical history and health information",
                        "Insurance details and payment information",
                        "Appointment booking details",
                        "Feedback and inquiries through contact forms"
                    ]
                },
                {
                    subtitle: "Information Collected Automatically:",
                    items: [
                        "IP address and device information",
                        "Browser type and operating system",
                        "Pages visited and time spent",
                        "Referring website and clickstream data",
                        "Cookies and similar tracking technologies"
                    ]
                }
            ]
        },
        {
            title: "3. How We Use Your Information",
            subsections: [
                {
                    subtitle: "We use the information we collect for:",
                    items: [
                        "Providing medical services and treatment",
                        "Managing appointments and scheduling",
                        "Processing payments and billing",
                        "Improving website functionality and user experience",
                        "Sending important healthcare updates and notifications",
                        "Complying with legal and regulatory requirements",
                        "Conducting research and statistical analysis",
                        "Preventing fraudulent activities"
                    ]
                }
            ]
        },
        {
            title: "4. Information Sharing and Disclosure",
            content: "We respect your privacy and do not sell, trade, or rent your personal information to third parties. However, we may share your information with: Healthcare providers and medical professionals involved in your care, Insurance companies for billing purposes, Legal authorities when required by law, Service providers who assist us in operations (under strict confidentiality agreements), Emergency responders in case of medical emergencies."
        },
        {
            title: "5. Data Security",
            content: "We implement comprehensive security measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction. These include: SSL encryption for data transmission, Secure authentication protocols, Regular security audits and updates, Access controls and role-based permissions, Secure backup systems. However, no security system is impenetrable, and we cannot guarantee absolute security."
        },
        {
            title: "6. Cookies and Tracking Technologies",
            content: "Our website uses cookies and similar tracking technologies to enhance your experience. Cookies help us remember your preferences, understand user behavior, and improve our services. You can control cookie settings through your browser, though some features may not function properly if cookies are disabled."
        },
        {
            title: "7. Patient Rights and Choices",
            subsections: [
                {
                    subtitle: "You have the right to:",
                    items: [
                        "Access your personal health information",
                        "Request correction of inaccurate information",
                        "Request deletion of your data (subject to legal requirements)",
                        "Opt-out of marketing communications",
                        "Request an accounting of disclosures",
                        "File a complaint regarding privacy violations"
                    ]
                }
            ]
        },
        {
            title: "8. Children's Privacy",
            content: "Our website is not intended for children under 13 years of age. We do not knowingly collect personal information from children. If we become aware that we have collected information from a child under 13, we will take steps to delete such information promptly. Parents or guardians who believe their child's information has been collected should contact us immediately."
        },
        {
            title: "9. Third-Party Links",
            content: "Our website may contain links to third-party websites. We are not responsible for the privacy practices of external sites. We encourage you to review the privacy policies of any third-party websites before providing your personal information."
        },
        {
            title: "10. Changes to This Privacy Policy",
            content: "We may update this Privacy Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by posting the updated policy on our website with a new effective date."
        },
        {
            title: "11. Contact Us",
            content: "If you have questions about this Privacy Policy or our privacy practices, please contact us at:"
        }
    ];

    return (
        <>
            <Navbar />
            
            {/* Hero Section */}
            <div className="relative w-full h-64 bg-gradient-to-r from-[#009CA6] to-[#00868C] flex items-center">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full -mr-48 -mt-48"></div>
                </div>
                <div className="container mx-auto px-4 relative z-10">
                    <div>
                        <div className="w-12 h-1 bg-[#AD3DC5] mb-3"></div>
                        <h1 className="text-4xl font-bold text-white">Privacy Policy</h1>
                        <p className="text-teal-100 mt-2">Your privacy is our priority</p>
                    </div>
                </div>
            </div>

            {/* Breadcrumb */}
            <div className="bg-gray-50 border-b border-gray-200">
                <div className="container mx-auto px-4 py-4">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Link to="/" className="text-[#009CA6] hover:text-[#00868C]">Home</Link>
                        <ChevronRight className="w-4 h-4" />
                        <span className="text-gray-800 font-medium">Privacy Policy</span>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="bg-white py-12">
                <div className="container mx-auto px-4 max-w-4xl">
                    {/* Last Updated */}
                    <div className="mb-8 p-4 bg-blue-50 border-l-4 border-[#009CA6] rounded">
                        <p className="text-sm text-gray-700">
                            <strong>Last Updated:</strong> December 1, 2024
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                            This Privacy Policy is effective as of the date above and applies to all users of our website and services.
                        </p>
                    </div>

                    {/* Content Sections */}
                    <div className="space-y-8">
                        {sections.map((section, index) => (
                            <div key={index} className="pb-8 border-b border-gray-200 last:border-b-0">
                                {/* Section Title */}
                                <h2 className="text-2xl font-bold text-[#1B3C74] mb-4 flex items-center gap-3">
                                    <span className="text-[#009CA6]">{section.title.split('.')[0]}.</span>
                                    <span>{section.title.split('. ')[1]}</span>
                                </h2>

                                {/* Simple Content */}
                                {section.content && (
                                    <p className="text-gray-700 leading-relaxed mb-4">
                                        {section.content}
                                    </p>
                                )}

                                {/* Subsections */}
                                {section.subsections && (
                                    <div className="space-y-4">
                                        {section.subsections.map((subsection, subIndex) => (
                                            <div key={subIndex}>
                                                <h3 className="font-semibold text-gray-800 mb-3">
                                                    {subsection.subtitle}
                                                </h3>
                                                <ul className="space-y-2 pl-6">
                                                    {subsection.items.map((item, itemIndex) => (
                                                        <li
                                                            key={itemIndex}
                                                            className="text-gray-700 flex items-start gap-3"
                                                        >
                                                            <span className="text-[#009CA6] font-bold mt-0.5">•</span>
                                                            <span>{item}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Contact Section - Special Handling */}
                                {index === sections.length - 1 && (
                                    <div className="mt-6 p-6 bg-gray-50 rounded-lg border border-gray-200">
                                        <div className="space-y-3">
                                            <div>
                                                <p className="font-semibold text-gray-800">Trauma Centre BHU</p>
                                                <p className="text-gray-600 text-sm">IMS, Banaras Hindu University</p>
                                            </div>
                                            <div>
                                                <p className="text-sm text-gray-600">
                                                    <strong>Phone:</strong>{' '}
                                                    <a href="tel:+91-9876543210" className="text-[#009CA6] hover:text-[#00868C]">
                                                        +91-9876543210
                                                    </a>
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-sm text-gray-600">
                                                    <strong>Email:</strong>{' '}
                                                    <a href="mailto:privacy@traumacentrebhu.com" className="text-[#009CA6] hover:text-[#00868C]">
                                                        privacy@traumacentrebhu.com
                                                    </a>
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Important Notice */}
                    <div className="mt-12 p-6 bg-amber-50 border-l-4 border-amber-500 rounded">
                        <h3 className="font-bold text-gray-800 mb-2">Important Notice</h3>
                        <p className="text-sm text-gray-700">
                            This Privacy Policy complies with applicable data protection regulations including the Information Technology Act, 2000, and other relevant Indian laws. If you have any concerns about our privacy practices or believe your rights have been violated, please contact us immediately.
                        </p>
                    </div>

                    {/* CTA Section */}
                    <div className="mt-12 bg-gradient-to-r from-[#009CA6] to-[#00868C] rounded-lg p-8 text-center">
                        <h3 className="text-2xl font-bold text-white mb-3">Still Have Questions?</h3>
                        <p className="text-teal-100 mb-6">
                            Our dedicated support team is ready to help you with any privacy-related concerns.
                        </p>
                        <Link
                            to="/contact-us"
                            className="inline-block bg-white text-[#009CA6] font-semibold px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                            Contact Us
                        </Link>
                    </div>
                </div>
            </div>

            <Footer />
        </>
    );
};

export default PrivacyPolicy;
